import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import omit from 'lodash/omit';

import getConditionalClasses, {
  CORE_CLASSNAME
} from '@adsk/bim360-matrix-react-helpers/es/getConditionalClasses';

import TextFieldCore from '@adsk/bim360-matrix-react-text-field-core';

import './_FloatingLabelTextField.scss';

const FLOATING_LABEL_TEXTFIELD_PROPS = ['label'];

/**
 * @class FloatingLabelTextField
 * Same thing as `TextFieldCore` but adds a cherry on top, and by cherry I mean a label. Floating.
 * Just like the magnificent Pickle Rick floating on top of the sewage water before dismantling a cockroach, only with a bit more elegance.
 *
 * If you plan on changing the font-size/font-weight using className, please use the `floating-label` mixin provided in `_FloatingLabelTextFieldMixins.scss`.
 **/
export default class FloatingLabelTextField extends PureComponent {
  /**
   * @constructor FloatingLabelTextField
   **/
  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      value: props.value
    };

    this._setTextFieldCoreRef = this._setTextFieldCoreRef.bind(this);

    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);

    this.focus = this.focus.bind(this);
    this.blur = this.blur.bind(this);
    this.select = this.select.bind(this);
  }

  onFocus(...args) {
    this.setState({ focused: true });
    this.props.onFocus(...args);
  }

  onBlur(...args) {
    this.setState({ focused: false });
    this.props.onBlur(this.state.value, ...args);
  }

  onChange(value) {
    this.setState({ value });
    this.props.onChange(value);
  }

  focus() {
    this.editor && this.editor.focus();
  }

  blur() {
    this.editor && this.editor.blur();
  }

  select() {
    this.editor && this.editor.select();
  }

  _setTextFieldCoreRef(textFieldCoreRef) {
    this.editor = textFieldCoreRef;
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const coreClassNames = ['FloatingLabelTextField', this.props.className];
    const classes = classnames(
      coreClassNames,
      getConditionalClasses(coreClassNames, {
        [`${CORE_CLASSNAME}--focused`]: this.state.focused,
        [`${CORE_CLASSNAME}--invalid`]: !this.props.valid
      })
    );
    const labelClasses = classnames('FloatingLabelTextField__label', {
      'FloatingLabelTextField__label--collapsed': this.state.focused || !!this.state.value
    });

    return (
      <div className={classes}>
        <label className={labelClasses}>{this.props.label}</label>
        <TextFieldCore
          {...omit(this.props, FLOATING_LABEL_TEXTFIELD_PROPS)}
          className="FloatingLabelTextField__text-field-core"
          ref={this._setTextFieldCoreRef}
          value={this.state.value}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
        />
      </div>
    );
  }
}

FloatingLabelTextField.defaultProps = {
  ...TextFieldCore.defaultProps
};

FloatingLabelTextField.propTypes = {
  ...TextFieldCore.propTypes,
  /**
   * Label to animate as a floating on top of the actual input.
   * NOTE: In case you want to change the font size/weight please use the floating-label mixin that's in _FloatingLabelTextFieldMixins.
   */
  label: PropTypes.string.isRequired
};
